export const TASK_FILTERS = ['today', 'overdue', 'upcoming', 'done', 'all'];

export const TASK_STATUSES = ['todo', 'in_progress', 'done'];
export const TASK_PRIORITIES = ['low', 'medium', 'high'];
export const TASK_TYPES = [
  'follow_up',
  'call',
  'whatsapp',
  'email',
  'appointment',
  'stock_check',
  'purchase_project',
  'internal_note',
  'other',
];

export const PRIORITY_VISUALS = {
  low: {
    flag: 'text-n-slate-10',
    completion: 'border-n-slate-8 bg-white text-transparent',
    badge: 'bg-n-slate-3 text-n-slate-11',
  },
  medium: {
    flag: 'text-n-amber-10',
    completion: 'border-n-amber-10 bg-n-amber-3 text-n-amber-10',
    badge: 'bg-n-amber-3 text-n-amber-11',
  },
  high: {
    flag: 'text-n-ruby-9',
    completion: 'border-n-ruby-9 bg-n-ruby-3 text-n-ruby-9',
    badge: 'bg-n-ruby-3 text-n-ruby-11',
  },
};

export const priorityFlagClass = priority =>
  PRIORITY_VISUALS[priority]?.flag ?? PRIORITY_VISUALS.low.flag;

export const priorityCompletionClass = priority =>
  PRIORITY_VISUALS[priority]?.completion ?? PRIORITY_VISUALS.low.completion;

export const priorityBadgeClass = priority =>
  PRIORITY_VISUALS[priority]?.badge ?? PRIORITY_VISUALS.low.badge;

export const startOfDay = date => {
  const value = new Date(date);
  value.setHours(0, 0, 0, 0);
  return value;
};

export const addDays = (date, days) => {
  const value = new Date(date);
  value.setDate(value.getDate() + days);
  return value;
};

export const isSameDay = (a, b) =>
  startOfDay(a).getTime() === startOfDay(b).getTime();

export const isDone = task => task.status === 'done';

export const isOverdue = (task, now = new Date()) =>
  !isDone(task) &&
  (task.dueTimePresent === false
    ? startOfDay(task.dueDate).getTime() < startOfDay(now).getTime()
    : new Date(task.dueDate).getTime() < now.getTime());

export const isDueToday = (task, now = new Date()) =>
  !isDone(task) && isSameDay(new Date(task.dueDate), now);

export const isUpcoming = (task, now = new Date()) =>
  !isDone(task) &&
  startOfDay(task.dueDate).getTime() > startOfDay(now).getTime();

export const overdueDays = (task, now = new Date()) => {
  const diff = startOfDay(now).getTime() - startOfDay(task.dueDate).getTime();
  return Math.max(1, Math.ceil(diff / 86400000));
};

export const priorityWeight = priority =>
  ({
    high: 3,
    medium: 2,
    low: 1,
  })[priority] ?? 0;

export const taskSort = (a, b, now = new Date()) => {
  if (isDone(a) !== isDone(b)) return isDone(a) ? 1 : -1;
  if (isOverdue(a, now) !== isOverdue(b, now)) {
    return isOverdue(a, now) ? -1 : 1;
  }
  if (isDueToday(a, now) !== isDueToday(b, now)) {
    return isDueToday(a, now) ? -1 : 1;
  }
  const priorityDiff = priorityWeight(b.priority) - priorityWeight(a.priority);
  if (priorityDiff !== 0) return priorityDiff;
  return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
};

export const matchesFilter = (task, filter, now = new Date()) => {
  if (filter === 'today') return isDueToday(task, now);
  if (filter === 'overdue') return isOverdue(task, now);
  if (filter === 'upcoming') return isUpcoming(task, now);
  if (filter === 'done') return isDone(task);
  return true;
};

export const formatShortDate = (date, locale = 'fr-FR') =>
  new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
  }).format(new Date(date));

export const formatTime = (date, locale = 'fr-FR') =>
  new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(date));

export const formatCurrency = (amount, currency = 'EUR', locale = 'fr-FR') => {
  if (!amount) return '';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
};
